import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { config } from "@/config";

/** /proof — the live scorecard, public. Every number recomputed from the same tables the
 *  product runs on (10-min cache server-side). This page exists because a speed claim you
 *  can't check is marketing. */

interface ProofRow {
  rule: string;
  catches: number;
  wireMatched: number;
  beatWirePct: number | null;
  medianLeadMin: number | null;
  scored: number;
  medianAbsZ: number | null;
  medianMovePct: number | null;
}

const LABEL: Record<string, string> = {
  "power-outage-impact": "Power outages (per company)",
  "outage-cluster": "Power outage clusters (market-wide)",
  "earthquake-near-asset": "Earthquakes near industrial assets",
  "quake-company-impact": "Earthquake per-company impacts",
  "industrial-fire-impact": "Industrial fires (satellite thermal)",
  "severe-event-near-asset": "Severe weather near assets",
};

export function ProofScreen() {
  const [rows, setRows] = useState<ProofRow[] | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    fetch(`${config.restUrl}/api/public/proof`)
      .then((r) => r.json())
      .then((j: { categories?: ProofRow[] }) => { if (alive) setRows(j.categories ?? []); })
      .catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, []);

  return (
    <div id="viewport" className="vp-fluid">
      <div className="legal">
        <header>
          <Link className="back" to="/">← EARLYWIRE</Link>
          <h1>The live scorecard</h1>
          <p className="upd">
            Recomputed continuously from production data. Lead time = our alert timestamp vs the first
            matched newswire item for the same event. Market move = the affected company's own equity,
            measured as the TRADEABLE move — the furthest excursion within 4 traded hours of the alert
            (older entries: next-session close-to-close), in σ of its normal move. No cherry-picking:
            every matched event since tracking began is in here.
          </p>
        </header>

        {err && <p>Scorecard temporarily unavailable — the numbers live at api.earlywire.io/api/public/proof.</p>}
        {!rows && !err && <p>Loading live numbers…</p>}
        {rows && (
          <div style={{ overflowX: "auto" }}>
            <table className="proof-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Alerts</th>
                  <th>Beat the wire</th>
                  <th>Median lead</th>
                  <th>Scored moves</th>
                  <th>Median move (σ)</th>
                  <th>Median move (%)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.rule}>
                    <td>{LABEL[r.rule] ?? r.rule}</td>
                    <td>{r.catches}</td>
                    <td>{r.beatWirePct != null ? `${r.beatWirePct}% of ${r.wireMatched}` : "—"}</td>
                    <td>{r.medianLeadMin != null ? `${r.medianLeadMin} min` : "—"}</td>
                    <td>{r.scored || "—"}</td>
                    <td>{r.medianAbsZ != null ? `${r.medianAbsZ.toFixed(2)}σ` : "learning"}</td>
                    <td>{r.medianMovePct != null ? `${r.medianMovePct.toFixed(2)}%` : "learning"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <h2>Honest footnotes</h2>
        <p>
          "Beat the wire" counts only events we could automatically match to a newswire item — unmatched
          events are excluded rather than assumed in our favour. Categories marked "learning" have fewer
          than a handful of scored moves and get no effect estimate in the product either. Fat-tailed
          categories (earthquakes, fires) are monitored for the rare large event; their median understates
          why they exist. Past performance of signals is not a prediction of future market behaviour.
        </p>
        <footer>
          <Link className="back" to="/">← Back to EARLYWIRE</Link>
        </footer>
      </div>
    </div>
  );
}
